import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'cdk-radial';
import Page from '../../../page';
import BackButton from '../../BackButton';
import { AddLocationsStepper } from './AddLocationsStepper';
import { setScreen } from '../../../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { screens } from '../../../config/uiFactory.config';
import { API_TYPE, PAGE_TITLE } from '../../../../constants/constants';
import './DAABSubscriptions.scss';
import {
  ACTIONS,
  manageSubscriptionUtil,
} from '../../utils/daabSubscriptions';
import {
  getApisSystemInfo,
  getSubscriptionsData,
  setSubscribeRequest,
  sendSubscribeRequest,
  getDMSDetailsForStore
} from '../../../../redux/connectedComponent/daabSubscriptions/daabSubscriptions.slice';
import { trackApiLocationEnabled, trackPageView, PAGE_SECTIONS, PAGE_SUBSECTIONS } from '../../../../utils/gtm';

export const AddLocations = (props) => {
  const { id, name, type, orgId, solutionData, webhookUrl, bundleLocations, bundleId } = props;
  const { id: appId } = solutionData;
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const { prevScreen } = state.uiFactorySlice;
  const { authenticationToken: token, role } = state?.loginDetailsSlice;
  const {
    subscriptions,
    fetchingSubscriptions,
    errorFetchingSubscriptions,
    dmsSupportedApis,
    subscribeRequest,
    unsubscribeRequest,
    subscribeRequestSent,
    fetchingSystemInfo,
    errorfetchingSystemInfo
  } = state?.daabSubscriptionsSlice;

  const isLoading = fetchingSubscriptions || fetchingSystemInfo;
  const hasError = errorFetchingSubscriptions || errorfetchingSystemInfo;

  const pendingLocationCount = useRef(0);

  const apiDMS = dmsSupportedApis[id];
  const apiNeedsDMS = !!apiDMS

  const ACTIVE_OR_PENDING_STATUSES = [
    'active',
    'enabled',
    'pending',
    'subscribed',
    'requested',
    'in review',
    'in_review',
  ];

  const isStatus = (status, allowedStatuses) =>
    allowedStatuses.includes((status || '').toLowerCase());

  const isSubscribedOrPending = (sub) => {
    const requestedStatus = (
      sub?.requested_status ||
      sub?.requestedStatus ||
      ''
    ).toLowerCase();

    return (
      isStatus(sub?.status, ACTIVE_OR_PENDING_STATUSES) ||
      requestedStatus === 'subscribe'
    );
  };

  const getSubscriptionEntityId = (sub) =>
    sub?.orgId || sub?.entityId || sub?.dataOwnerOrgId;

  const getApiScopeKey = (apiId, apiType) => `${apiId || ''}::${apiType || ''}`;
  const currentApiScopeKey = useMemo(
    () => getApiScopeKey(id, type),
    [id, type]
  );

  const activeSubByEntityId = useMemo(
    () => Object.fromEntries(
      subscriptions
        ?.filter((sub) => {
          const hasCurrentApiScope =
            currentApiScopeKey && currentApiScopeKey !== '::';
          const isCurrentApi = hasCurrentApiScope
            ? sub?.optimisticApiScopeKey === currentApiScopeKey
            : true;
          return isCurrentApi && isSubscribedOrPending(sub);
        })
        ?.map((sub) => [getSubscriptionEntityId(sub), sub])
        ?.filter(([entityId]) => !!entityId)
    ),
    [subscriptions, currentApiScopeKey]
  );

  useEffect(() => {
    trackPageView({
      page_section: PAGE_SECTIONS.MY_APIS,
      page_subsection: PAGE_SUBSECTIONS.MANAGE_API_LOCATIONS,
    });
  }, []);

  const onBack = () => {
    dispatch(setScreen({ screen: prevScreen, additionalData: { id, type, webhookUrl, name, orgId, bundleId, bundleLocations } }));
  };
  
  const handleLocationSelection = (args) => {
    const {locationData, cmf} = args;
    const storeId = locationData.storeId;
    const { updatedSubReq } = manageSubscriptionUtil({
      subscribeRequest,
      unsubscribeRequest,
      existingSubcriptions: activeSubByEntityId,
      ...args,
    });

    dispatch(setSubscribeRequest(updatedSubReq));
    const cmfNumber = cmf || locationData?.cmfNumber;
    if (apiNeedsDMS && storeId && args.action !== ACTIONS.UPDATE_CMF)
      dispatch(
        getDMSDetailsForStore({
          token,
          cmfNumber,
          storeId,
          entityId: locationData.entityId,
          apiDMS,
        })
      );
  };

  const handleSave = () => {
    const { id: appId, name: appName } = solutionData;
    const allowedBundleOrgIds = new Set(
      (bundleLocations || [])
        .filter((loc) => loc.bundleId === bundleId)
        .map((loc) => loc.orgId)
    );

    const newEntityList = Object.values(subscribeRequest).filter((loc) => {
      const isAlreadySubscribed = isSubscribedOrPending(
        activeSubByEntityId[loc.entityId]
      );

      if (isAlreadySubscribed) {
        return false;
      }

      if (bundleId) {
        return allowedBundleOrgIds.has(loc.entityId);
      }

      return true;
    });

    if (newEntityList.length) {
      pendingLocationCount.current = newEntityList.length;
      const payload = {
        appId,
        appName,
        entityIdList: newEntityList,
        api: { id, orgId, type},  
        businessTitle: role
      };
      dispatch(sendSubscribeRequest({ payload, token }));
    }
  };

  useEffect(() => {
    const { id: appId } = solutionData;
    if (!bundleId) {
      dispatch(setSubscribeRequest({}));
    }
    const apis = [{id}];
    if (type === API_TYPE.ASYNC) 
      apis[0]['apiType'] = type;
    dispatch(getApisSystemInfo({ apis, token }));
    dispatch(
      getSubscriptionsData({
        solutionId: appId,
        apiId: id,
        apiType: type,
        token,
      })
    );
  }, [solutionData, id, type, token, dispatch, bundleId]);

 

  useEffect(() => {
    if (Object.keys(subscribeRequest).length && subscribeRequestSent) {
      if (pendingLocationCount.current > 0) {
        trackApiLocationEnabled({
          apiId: id,
          apiDisplayName: name,
          apiType: type,
          locationCount: pendingLocationCount.current,
        });
        pendingLocationCount.current = 0;
      }
      dispatch(setScreen({ screen: screens.MY_APIS }));
    }
  }, [subscribeRequest, subscribeRequestSent, dispatch, id, name, type]);

  return (
    <Page pageHeader={PAGE_TITLE.MY_APIS}>
      <BackButton onBack={onBack} />
      <div className="main-menu-container">
        <div className="add-locations-page">
          <section className="add-locations-page-header-container">
            <div className="heading">Add Locations for {name}</div>
            <div className="add-locations-subheader">
              Activate the locations of your Dealerships with this APIs.
            </div>
          </section>
          {isLoading ? (
            <div className="add-locations-loader">
              <Loader />
            </div>
          ) : hasError ? (
            <div className="add-locations-loader">
              Error while fetching data, try again after sometime.
            </div>
          ) : (
            <section>
              <AddLocationsStepper
                apiNeedsDMS={apiNeedsDMS}
                handleLocationSelection={handleLocationSelection}
                selectedAPI={{ id, name, type }}
                handleCompleteActivation={handleSave}
                bundleLocations={bundleLocations}
                bundleId={bundleId}
                activeSubByEntityId={activeSubByEntityId}
              />
            </section>
          )}
        </div>
      </div>
    </Page>
  );
};
